import { useState } from "react";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { Button } from "@/components/ui/button";
import { formatInt, formatPercent } from "@/lib/statsFormat";

const TOP_N = 5;
const SLICE_COLORS = [
  "var(--chart-1)",
  "var(--chart-2)",
  "var(--chart-3)",
  "var(--chart-4)",
  "var(--chart-5)",
];
const OTHER_COLOR = "var(--chart-6)";

export interface DonutItem {
  name: string;
  value: number;
}

interface DonutBreakdownProps {
  items: DonutItem[];
  unitLabel: string;
  emptyText?: string;
}

const DonutBreakdown = ({
  items,
  unitLabel,
  emptyText = "No calls of this kind in this range",
}: DonutBreakdownProps) => {
  const [expanded, setExpanded] = useState(false);

  const total = items.reduce((acc, item) => acc + item.value, 0);
  const top = items.slice(0, TOP_N);
  const rest = items.slice(TOP_N);
  const restValue = rest.reduce((acc, item) => acc + item.value, 0);

  const slices = top.map((item, index) => ({
    ...item,
    color: SLICE_COLORS[index],
  }));
  if (restValue > 0) {
    slices.push({ name: "Other", value: restValue, color: OTHER_COLOR });
  }

  const legend = expanded
    ? [
        ...slices.slice(0, top.length),
        ...rest.map((item) => ({ ...item, color: OTHER_COLOR })),
      ]
    : slices;

  if (total === 0) {
    return (
      <p className="py-6 text-center text-sm text-muted-foreground">{emptyText}</p>
    );
  }

  return (
    <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-start">
      <div className="relative size-40 shrink-0">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={slices}
              dataKey="value"
              nameKey="name"
              innerRadius="60%"
              outerRadius="100%"
              paddingAngle={1}
              stroke="none"
              isAnimationActive={false}
            >
              {slices.map((slice) => (
                <Cell key={slice.name} fill={slice.color} />
              ))}
            </Pie>
            <Tooltip
              formatter={(value: number, name: string) => [
                `${formatInt(value)} · ${formatPercent((value / total) * 100)}`,
                name,
              ]}
              contentStyle={{
                background: "var(--popover)",
                border: "1px solid var(--border)",
                borderRadius: 6,
              }}
            />
          </PieChart>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-lg font-bold">{formatInt(total)}</span>
          <span className="text-xs text-muted-foreground">{unitLabel}</span>
        </div>
      </div>

      <div className="w-full min-w-0 flex-1">
        <ul className="max-h-72 space-y-1.5 overflow-y-auto text-sm">
          {legend.map((item, index) => (
            <li key={index} className="flex items-center justify-between gap-3">
              <span className="flex min-w-0 items-center gap-2">
                <span
                  className="size-2.5 shrink-0 rounded-full"
                  style={{ background: item.color }}
                />
                <span className="truncate">{item.name}</span>
              </span>
              <span className="shrink-0 tabular-nums text-muted-foreground">
                {formatInt(item.value)} · {formatPercent((item.value / total) * 100)}
              </span>
            </li>
          ))}
        </ul>
        {rest.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            className="mt-2"
            onClick={() => setExpanded((value) => !value)}
          >
            {expanded ? "Show less" : `Show all (${items.length})`}
          </Button>
        )}
      </div>
    </div>
  );
};

export default DonutBreakdown;
